'use client';

import { useMemo } from 'react';
import { AssetFilterWithSearch } from '../../Inputs/AssetFilterWithSearch';
import Checkbox from '../../primitives/Checkbox/Checkbox';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { usePerpMarketsOnlyTabs } from 'src/constants/historyTables/filterConfigs';
import { AssetListAssetWithMarketIndex } from 'src/@types/assets';
import { UIMarket } from '@velocity-exchange/common';
import OrderHistoryOrderStatusFilter, {
	OrderStatusFilter,
} from 'src/components/HistoryTables/TradePageOrderHistoryTable/OrderHistoryOrderStatusFilter';

type OrderHistoryFiltersProps = {
	overviewPage?: boolean;
};

export const OrderHistoryFilters = (_props: OrderHistoryFiltersProps) => {
	const filters = useHistoryTableFilterStore((s) => s.getFilterState('orders'));
	const setHistoryTableFilterState = useHistoryTableFilterStore((s) => s.set);
	const isMobile = useIsMobileScreenSize();

	const perpMarketsOnlyTabs = usePerpMarketsOnlyTabs({
		withBaseAssetSymbol: true,
	});

	const assetFilterOptions = useMemo<AssetListAssetWithMarketIndex[]>(() => {
		return perpMarketsOnlyTabs.flatMap(
			(market): AssetListAssetWithMarketIndex[] => {
				if (market.value === 'all_markets') {
					return [
						{
							mint: 'all_markets',
							ticker: 'all_markets',
							displayLabel: 'All Markets',
							marketIndex: -1,
						},
					];
				}

				const uiMarket = UIMarket.perpMarkets.find(
					(config) => config.symbol === market.value
				);

				// Drop tabs we can't resolve to a real market rather than silently
				// pointing the filter at market index 0 (the first perp).
				if (!uiMarket) {
					return [];
				}

				return [
					{
						mint: undefined,
						ticker: market.value,
						displayLabel: market.label,
						marketIndex: uiMarket.marketIndex,
					},
				];
			}
		);
	}, [perpMarketsOnlyTabs]);

	const handleOrderStatusFilterChange = (selectedFilter: OrderStatusFilter) => {
		setHistoryTableFilterState((s) => {
			s.orders.showOnlyFilledOrders = selectedFilter === 'filled';
		});
	};

	const handleMarketFilterChange = (selectedMarket: string) => {
		setHistoryTableFilterState((s) => {
			s.orders.marketSymbol = selectedMarket;
		});
	};

	const handleFilledOrdersToggle = () => {
		setHistoryTableFilterState((s) => {
			s.orders.showOnlyFilledOrders = !s.orders.showOnlyFilledOrders;
		});
	};

	const currentFilledOrdersValue: OrderStatusFilter =
		filters.showOnlyFilledOrders ? 'filled' : 'all';

	if (isMobile) {
		return (
			<div className="flex w-full flex-col gap-y-md">
				<div className="flex w-full items-center justify-between gap-x-sm">
					<OrderHistoryOrderStatusFilter
						className="flex-1"
						currentFilledOrdersValue={currentFilledOrdersValue}
						handleTableFilterChange={handleOrderStatusFilterChange}
					/>
				</div>
				<AssetFilterWithSearch
					id="order-history-market-filter"
					currentSelection={filters.marketSymbol}
					options={assetFilterOptions}
					onChange={handleMarketFilterChange}
					label="Market"
					className="w-full"
				/>
			</div>
		);
	}

	return (
		<div className="flex w-full items-center justify-between">
			<div className="flex items-center gap-x-md">
				<AssetFilterWithSearch
					id="order-history-market-filter"
					currentSelection={filters.marketSymbol}
					options={assetFilterOptions}
					onChange={handleMarketFilterChange}
					label="Market"
					className="min-w-[200px]"
				/>
			</div>
			<Checkbox.Default
				id="show-only-filled-orders"
				label="Show only filled orders"
				checked={filters.showOnlyFilledOrders}
				onCheckedChange={handleFilledOrdersToggle}
			/>
		</div>
	);
};
